import { ReactNode, useEffect, useState } from "react";
import { AddClientDialog } from "@/components/AddClientDialog";
import { AddLoanDialog } from "@/components/AddLoanDialog";
import { useNavigate, useLocation, NavLink as RouterNavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Wallet,
  CalendarClock,
  Sparkles,
  BarChart3,
  Settings,
  Building2,
  LogOut,
  Menu,
  X,
  Calendar as CalendarIcon,
  ListTodo,
  Plus,
  UserPlus,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface NavItem {
  label: string;
  icon: typeof LayoutDashboard;
  to: string;
  badge?: number;
}

interface AppShellProps {
  children: ReactNode;
  overdueCount?: number;
  userName?: string;
  userRole?: string;
}

export function AppShell({ children, overdueCount = 0, userName = "Usuário", userRole = "Administrador" }: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState<number>(0);

  // Close drawer when route changes
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [drawerOpen]);

  const handleLogout = async () => {
    try {
      await Promise.race([
        supabase.auth.signOut({ scope: "local" }),
        new Promise((resolve) => setTimeout(resolve, 4000)),
      ]);
      toast.success("Logout realizado com sucesso!");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      navigate("/auth");
    }
  };

  const principal: NavItem[] = [
    { label: "Dashboard", icon: LayoutDashboard, to: "/dashboard" },
    { label: "Clientes", icon: Users, to: "/clientes" },
    { label: "Vencimentos", icon: CalendarClock, to: "/vencimentos", badge: overdueCount },
    { label: "Previsão de pagamento", icon: ListTodo, to: "/todo" },
  ];

  const tools: NavItem[] = [
    { label: "Central IA", icon: Sparkles, to: "/central-inteligente" },
    { label: "Calendário", icon: CalendarIcon, to: "/calendario" },
    { label: "Relatórios", icon: BarChart3, to: "/relatorios" },
    { label: "Configurações", icon: Settings, to: "/configuracoes" },
  ];

  const initials = (userName || "U").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  const NavLink = ({ item }: { item: NavItem }) => {
    const Icon = item.icon;
    return (
      <RouterNavLink
        to={item.to}
        end={item.to === "/"}
        onClick={() => setDrawerOpen(false)}
        className={({ isActive: active }) =>
          `group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
            active
              ? "bg-gradient-to-r from-[hsl(225_85%_56%)] to-[hsl(228_80%_48%)] text-white shadow-[0_8px_22px_-8px_hsl(225_75%_53%/0.7)]"
              : "text-[hsl(228_25%_78%)] hover:text-white hover:bg-white/[0.04]"
          }`
        }
      >
        {({ isActive: active }) => (
          <>
            <Icon className={`h-[18px] w-[18px] shrink-0 ${active ? "text-white" : "text-[hsl(228_25%_70%)] group-hover:text-white"}`} strokeWidth={2} />
            <span className="flex-1 text-left truncate">{item.label}</span>
            {item.badge !== undefined && item.badge > 0 && (
              <span className="ml-auto inline-flex items-center justify-center min-w-[22px] h-[20px] px-1.5 rounded-full bg-[hsl(4_75%_58%)] text-white text-[10px] font-bold shadow-[0_4px_12px_-2px_hsl(4_75%_58%/0.6)]">
                {item.badge}
              </span>
            )}
          </>
        )}
      </RouterNavLink>
    );
  };

  const sidebarStyle = {
    background:
      "radial-gradient(at 0% 0%, hsl(225 85% 30% / 0.55) 0px, transparent 55%), linear-gradient(180deg, hsl(225 64% 10%) 0%, hsl(225 60% 14%) 100%)",
    borderRight: "1px solid hsl(228 50% 18% / 0.6)",
  } as const;

  const SidebarInner = ({ withClose = false }: { withClose?: boolean }) => (
    <>
      {/* Logo */}
      <div className="px-5 pt-6 pb-5 flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-[hsl(225_85%_55%)] to-[hsl(228_80%_42%)] flex items-center justify-center shadow-[0_10px_24px_-6px_hsl(225_85%_45%/0.7)]">
          <Building2 className="h-5 w-5 text-white" strokeWidth={2.2} />
        </div>
        <div className="leading-tight flex-1">
          <div className="text-[15px] font-semibold text-white tracking-tight">Gestão de</div>
          <div className="text-[15px] font-semibold text-white tracking-tight -mt-0.5">Empréstimos</div>
          <div className="text-[9.5px] font-bold text-[hsl(228_25%_60%)] uppercase tracking-[0.22em] mt-1">
            Wealth · Capital
          </div>
        </div>
        {withClose && (
          <button
            onClick={() => setDrawerOpen(false)}
            aria-label="Fechar menu"
            className="h-9 w-9 rounded-lg flex items-center justify-center text-[hsl(228_25%_78%)] hover:text-white hover:bg-white/[0.06] transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-2">
        <div className="text-[10px] font-bold text-[hsl(228_20%_50%)] uppercase tracking-[0.18em] px-3 pt-3 pb-2">
          Principal
        </div>
        <div className="space-y-1">
          {principal.map((item) => (
            <NavLink key={item.label} item={item} />
          ))}
        </div>

        <div className="px-3 pt-4 pb-2 flex gap-2">
          <AddClientDialog
            onSuccess={() => setRefetchTrigger(n => n + 1)}
            trigger={
              <button className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl text-xs font-medium border border-white/[0.08] bg-white/[0.03] text-[hsl(228_25%_78%)] hover:bg-white/[0.07] hover:text-white transition-colors px-2 py-2">
                <UserPlus className="h-3.5 w-3.5" />
                Cadastrar Cliente
              </button>
            }
          />
          <AddLoanDialog
            onSuccess={() => setRefetchTrigger(n => n + 1)}
            trigger={
              <button className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl text-xs font-medium bg-gradient-to-br from-[hsl(225_85%_56%)] to-[hsl(228_80%_48%)] text-white shadow-[0_4px_14px_-4px_hsl(225_75%_53%/0.6)] hover:shadow-[0_6px_20px_-4px_hsl(225_75%_53%/0.7)] transition-all px-2 py-2">
                <Plus className="h-3.5 w-3.5" />
                Adicionar Empréstimo
              </button>
            }
          />
        </div>

        <div className="text-[10px] font-bold text-[hsl(228_20%_50%)] uppercase tracking-[0.18em] px-3 pt-6 pb-2">
          Ferramentas
        </div>
        <div className="space-y-1">
          {tools.map((item) => (
            <NavLink key={item.label} item={item} />
          ))}
        </div>
      </nav>

      {/* User */}
      <div className="px-3 pb-4 pt-3 border-t border-white/[0.06]">
        <div className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/[0.04] transition-colors">
          <div className="h-9 w-9 rounded-full avatar-grad-1 flex items-center justify-center text-[12px] font-bold ring-2 ring-white/10">
            {initials}
          </div>
          <div className="flex-1 min-w-0 leading-tight">
            <div className="text-[13px] font-semibold text-white truncate">{userName}</div>
            <div className="text-[11px] text-[hsl(228_20%_60%)] truncate">{userRole}</div>
          </div>
          <button
            onClick={handleLogout}
            title="Sair"
            className="text-[hsl(228_20%_60%)] hover:text-white transition-colors p-1"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background lg:pl-[236px]">
      {/* Sidebar — fixed to viewport (desktop only) */}
      <aside
        className="hidden lg:flex w-[236px] flex-col text-[hsl(228_30%_88%)] fixed inset-y-0 left-0 h-screen z-30"
        style={sidebarStyle}
      >
        <SidebarInner />
      </aside>

      {/* Mobile top bar with hamburger */}
      <div className="lg:hidden sticky top-0 z-20 flex items-center gap-3 px-4 h-14 bg-background/85 backdrop-blur border-b border-border">
        <button
          onClick={() => setDrawerOpen(true)}
          aria-label="Abrir menu"
          className="h-10 w-10 rounded-lg flex items-center justify-center text-foreground hover:bg-muted transition-colors -ml-2"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-[hsl(225_85%_55%)] to-[hsl(228_80%_42%)] flex items-center justify-center shadow-[0_6px_16px_-4px_hsl(225_85%_45%/0.6)]">
            <Building2 className="h-4 w-4 text-white" strokeWidth={2.2} />
          </div>
          <span className="text-sm font-semibold tracking-tight text-foreground truncate">Gestão de Empréstimo Mv</span>
        </div>
        {overdueCount > 0 && (
          <span className="ml-auto inline-flex items-center justify-center min-w-[22px] h-[20px] px-1.5 rounded-full bg-[hsl(4_75%_58%)] text-white text-[10px] font-bold">
            {overdueCount}
          </span>
        )}
      </div>

      {/* Mobile drawer + backdrop */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-200 ${
          drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{ backgroundColor: "rgba(16,24,40,0.5)" }}
        onClick={() => setDrawerOpen(false)}
        aria-hidden={!drawerOpen}
      />
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-[280px] flex flex-col text-[hsl(228_30%_88%)] transform transition-transform duration-[250ms] ease-out ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={sidebarStyle}
        role="dialog"
        aria-modal="true"
        aria-label="Menu de navegação"
      >
        <SidebarInner withClose />
      </aside>

      {/* Main */}
      <main className="min-w-0">{children}</main>
    </div>
  );
}